import { Injectable, inject } from '@angular/core';
import {
    AppRideStatus,
    RideCategory,
    RideEntryType,
    RideStatus,
    flatMap,
    getRideKilometersPerGait,
    getRideMinutesPerGait,
    wayPointKilometers
} from '@caballus/common';
import {
    CurrentRide,
    HorseIdentityWithGaits,
    NetworkStatusService,
    Ride,
    RideGaitMetrics,
    RideService,
    User,
    WayPoint
} from '@caballus/ui-common';
import { Location } from '@capacitor-community/background-geolocation';
import { Platform } from '@ionic/angular';
import { isNil } from 'lodash';
import moment from 'moment';
import { createObjectID } from 'mongo-object-reader';
import { ToastrService } from 'ngx-toastr';
import {
    BehaviorSubject,
    EMPTY,
    Observable,
    Subscription,
    catchError,
    concat,
    defer,
    filter,
    finalize,
    firstValueFrom,
    from,
    interval,
    lastValueFrom,
    map,
    of,
    switchMap,
    take,
    tap,
    throwError,
    timeout
} from 'rxjs';
import { RideCache } from '../caches/ride/ride.cache';
import { StorageService } from './storage.service';

const SYNC_INTERVAL = 5000; // 5 seconds
const SYNC_TIMEOUT = 30000; // 30 seconds
const MAX_RETRY_ATTEMPTS = 3;

export class RideSyncError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'RideSyncError';
    }

    static noCurrentRide(): RideSyncError {
        return new RideSyncError('No current ride');
    }

    static invalidStatus(
        current: AppRideStatus,
        expected?: AppRideStatus | AppRideStatus[]
    ): RideSyncError {
        let message = 'Ride not in correct status.';
        if (expected) {
            const currentMessage = `Found '${current}' instead`;
            const expectedMessage = Array.isArray(expected)
                ? `Expected one of: ${expected.join(', ')}`
                : `Expected: ${expected}`;
            message = `${message} ${expectedMessage}. ${currentMessage}`;
        }
        return new RideSyncError(message);
    }

    static syncError(message: string): RideSyncError {
        return new RideSyncError(`Sync error: ${message}`);
    }
}

export interface SaveRideDetails {
    manualEntry?: { duration: number; distance: number };
    category: RideCategory;
    notes: string;
}

interface SyncQueueItem {
    rideId: string;
    lastAttempt: Date;
    retryCount: number;
    priority: 'high' | 'normal'; // high for end ride, normal for updates
}

@Injectable({
    providedIn: 'root'
})
export class RideSyncService {
    private readonly storageService = inject(StorageService);
    private readonly rideService = inject(RideService);
    private readonly rideCache = inject(RideCache);
    private readonly networkStatus = inject(NetworkStatusService);
    private readonly toastr = inject(ToastrService);
    private readonly platform = inject(Platform);

    private currentRideSubject = new BehaviorSubject<CurrentRide | null>(null);
    public currentRide$ = this.currentRideSubject.asObservable();

    private syncQueue = new Map<string, SyncQueueItem>();
    private syncInProgress = false;
    private lastStoredRideJson = '';
    private waypointBuffer: WayPoint[] = [];
    private addWaypointCalled: number = 0;
    private readonly WAYPOINT_BUFFER_SIZE = 5;
    private readonly WAYPOINT_FLUSH_INTERVAL_MS = 15000; // 15 seconds
    private waypointFlushSub: Subscription;
    private rideDirty = false;
    private waypointChunkIndex = 0; // Track chunk index for current ride

    constructor() {
        this.init();
        // Start periodic buffer flush
        this.waypointFlushSub = interval(this.WAYPOINT_FLUSH_INTERVAL_MS).subscribe(() => {
            this.flushWaypointBuffer();
        });

        this.platform.pause.subscribe(() => {
            this.flushWaypointBuffer(true);
        });

        this.platform.resume.subscribe(() => {
            this.flushWaypointBuffer(true);
        });
    }

    private async init(): Promise<void> {
        const savedRide = await this.storageService.getCurrentRide();

        if (!savedRide || savedRide.appRideStatus === AppRideStatus.Finalized) {
            await this.storageService.clearCurrentRideData();
            return;
        }

        // Initialize chunk index from stored chunks
        if (savedRide._id) {
            this.waypointChunkIndex = await this.storageService.getWaypointChunkCount(
                savedRide._id
            );
        }

        this.currentRideSubject.next(savedRide);
        this.lastStoredRideJson = JSON.stringify(savedRide);

        // Initialize sync queue only if we're online
        if (this.networkStatus.currentNetworkStatus.isOnline) {
            this._initSyncQueue();
        }

        // Listen for network status changes
        this.networkStatus.continuousNetworkStatus$
            .pipe(
                filter(status => status.isOnline && !this.syncInProgress),
                switchMap(() => this._processQueue())
            )
            .subscribe();
    }

    private _shouldSync(ride: CurrentRide): boolean {
        const rideJson = JSON.stringify(ride);
        if (rideJson !== this.lastStoredRideJson) {
            this.lastStoredRideJson = rideJson;
            return true;
        }
        return false;
    }

    private _initCurrentRideSynchronization(): void {
        interval(SYNC_INTERVAL)
            .pipe(
                switchMap(() => {
                    if (this.currentRideSubject.value) {
                        return from(
                            this.storageService.setCurrentRide(this.currentRideSubject.value)
                        ).pipe(
                            catchError(error => {
                                console.error('Failed to sync current ride:', error);
                                return EMPTY;
                            })
                        );
                    }
                    return EMPTY;
                })
            )
            .subscribe();
    }

    private _initSyncQueue(): void {
        // Only process queue when online and not already syncing
        this.networkStatus.continuousNetworkStatus$
            .pipe(
                filter(status => status.isOnline && !this.syncInProgress),
                switchMap(() => this._processQueue())
            )
            .subscribe();
    }

    private _processQueue(): Observable<void> {
        if (this.syncInProgress || this.syncQueue.size === 0) {
            return EMPTY;
        }

        this.syncInProgress = true;

        // Sort queue by priority and retry count
        const sortedQueue = Array.from(this.syncQueue.values()).sort((a, b) => {
            if (a.priority !== b.priority) {
                return a.priority === 'high' ? -1 : 1;
            }
            return a.retryCount - b.retryCount;
        });

        return concat(
            ...sortedQueue.map(item =>
                this._processSyncItem(item).pipe(
                    tap({
                        next: () => {
                            // Success - remove from queue
                            this.syncQueue.delete(item.rideId);
                        },
                        error: error => {
                            // Update retry count and last attempt
                            const queueItem = this.syncQueue.get(item.rideId);
                            if (queueItem) {
                                queueItem.retryCount++;
                                queueItem.lastAttempt = new Date();

                                // Remove if max retries reached
                                if (queueItem.retryCount >= MAX_RETRY_ATTEMPTS) {
                                    this.syncQueue.delete(item.rideId);
                                    console.error(
                                        `[RideSync] Max retries reached for ride ${item.rideId}:`,
                                        error
                                    );
                                }
                            }
                        }
                    }),
                    catchError(() => EMPTY) // Continue with next item regardless of error
                )
            )
        ).pipe(
            finalize(() => {
                this.syncInProgress = false;
            })
        );
    }

    private _processSyncItem(item: SyncQueueItem): Observable<void> {
        return defer(() => from(this.storageService.getUnsyncedRides())).pipe(
            map(rides => rides.find(r => r._id === item.rideId)),
            switchMap(ride => {
                if (!ride) {
                    return of(void 0);
                }

                // Handle different ride statuses
                switch (ride.appRideStatus) {
                    case AppRideStatus.Riding:
                        // For active rides, just sync the basic info
                        return this.rideService
                            .startRide({
                                _id: ride._id,
                                horseIds: ride.horseIdentities.map(h => h._id),
                                startDateTime: ride.startDateTime
                            })
                            .pipe(
                                timeout(SYNC_TIMEOUT),
                                switchMap(() =>
                                    from(this.storageService.removeUnsyncedRide(item.rideId))
                                ),
                                map(() => void 0)
                            );

                    case AppRideStatus.EndRideDetails:
                        // For ended rides, sync the end state
                        return this.rideService
                            .endRide({
                                _id: ride._id,
                                startDateTime: ride.startDateTime,
                                endDateTime: ride.endDateTime,
                                horseIds: ride.horseIdentities.map(h => h._id),
                                paths: ride.paths,
                                pauseTimeInSeconds: ride.pauseTimeInSeconds,
                                distanceKilometers: ride.distanceKilometers
                            })
                            .pipe(
                                timeout(SYNC_TIMEOUT),
                                switchMap(() =>
                                    from(this.storageService.removeUnsyncedRide(item.rideId))
                                ),
                                map(() => void 0)
                            );

                    case AppRideStatus.Finalized:
                        // For finalized rides, sync the complete details
                        return this.rideService.saveRideDetails(ride).pipe(
                            timeout(SYNC_TIMEOUT),
                            switchMap(() =>
                                from(this.storageService.removeUnsyncedRide(item.rideId))
                            ),
                            map(() => void 0)
                        );

                    default:
                        console.error(`[RideSync] Unexpected ride status: ${ride.appRideStatus}`);
                        return throwError(
                            () => new Error(`Unexpected ride status: ${ride.appRideStatus}`)
                        );
                }
            }),
            catchError(error => {
                console.error('[RideSync] Error processing sync item:', error);
                return throwError(() => error);
            })
        );
    }

    private _addToSyncQueue(rideId: string, priority: 'high' | 'normal' = 'normal'): void {
        const existingItem = this.syncQueue.get(rideId);
        if (existingItem) {
            // Update priority if needed
            if (priority === 'high') {
                existingItem.priority = priority;
            }
            return;
        }

        // Check if ride exists in unsynced rides before adding to queue
        this.storageService.getUnsyncedRides().then(rides => {
            const rideExists = rides.some(r => r._id === rideId);
            if (!rideExists) {
                return;
            }

            this.syncQueue.set(rideId, {
                rideId,
                lastAttempt: null,
                retryCount: 0,
                priority
            });

            // If online and not syncing, process queue immediately
            if (this.networkStatus.currentNetworkStatus.isOnline && !this.syncInProgress) {
                this._processQueue().subscribe();
            }
        });
    }

    public async startRide(details: {
        horseIdentities: HorseIdentityWithGaits[];
        riderIdentity: User;
        name: string;
    }): Promise<void> {
        const currentRide = this.currentRideSubject.value;
        if (currentRide) {
            this.toastr.error('A ride is already in progress.');
            throw RideSyncError.invalidStatus(currentRide.appRideStatus);
        }

        const newRide = new Ride({
            _id: createObjectID(),
            appRideStatus: AppRideStatus.Riding,
            rideStatus: RideStatus.InProgress,
            startDateTime: new Date(),
            horseIdentities: details.horseIdentities,
            riderIdentity: details.riderIdentity.toIdentity(),
            paths: [
                {
                    startDateTime: new Date(),
                    endDateTime: undefined,
                    wayPoints: []
                }
            ]
        });

        // Reset chunk index for new ride
        this.waypointChunkIndex = 0;

        try {
            await this.updateCurrentRide(newRide);
            if (!this.networkStatus.currentNetworkStatus.isOnline) {
                await this.storageService.storeUnsyncedRide(newRide);
                this._addToSyncQueue(newRide._id);
                return;
            }
            const result = await lastValueFrom(
                this.rideService
                    .startRide({
                        _id: newRide._id,
                        horseIds: newRide.horseIdentities.map(h => h._id),
                        startDateTime: newRide.startDateTime
                    })
                    .pipe(
                        timeout(5000),
                        catchError(error => {
                            console.error('[RideSync] Failed to sync new ride:', error);
                            return of(null);
                        })
                    )
            );
            if (!result) {
                await this.storageService.storeUnsyncedRide(newRide);
                this._addToSyncQueue(newRide._id);
            }
        } catch (error) {
            console.error('[RideSync] Error in startRide:', error);
            await this.storageService.storeUnsyncedRide(newRide);
            this._addToSyncQueue(newRide._id);
        }
    }

    public async updateCurrentRide(ride: CurrentRide): Promise<void> {
        if (this.rideDirty) {
            await this.storageService.setCurrentRide(ride);
            this.rideDirty = false;
            // Queue for sync in specific cases:
            // 1. When offline
            // 2. When ride is first created (Riding status)
            // 3. When ride is ended (EndRideDetails status)
            // 4. When ride is finalized
            if (
                !this.networkStatus.currentNetworkStatus.isOnline ||
                ride.appRideStatus === AppRideStatus.Riding ||
                ride.appRideStatus === AppRideStatus.EndRideDetails ||
                ride.appRideStatus === AppRideStatus.Finalized
            ) {
                this._addToSyncQueue(
                    ride._id,
                    // High priority for end and finalize operations
                    ride.appRideStatus === AppRideStatus.EndRideDetails ||
                        ride.appRideStatus === AppRideStatus.Finalized
                        ? 'high'
                        : 'normal'
                );
            }
        }
        this.currentRideSubject.next(ride);
    }

    // Flush any buffered waypoints to storage as chunks
    private async flushWaypointBuffer(force = false): Promise<void> {
        if (this.waypointBuffer.length === 0) return;
        if (!force && this.waypointBuffer.length < this.WAYPOINT_BUFFER_SIZE) return;
        const currentRide = this.currentRideSubject.value;
        if (!currentRide || currentRide.appRideStatus !== AppRideStatus.Riding) {
            this.waypointBuffer = [];
            return;
        }

        // Store waypoints as a chunk
        const chunkIndex = await this.storageService.incrementWaypointChunkCount(currentRide._id);
        console.log(
            'Flushing waypoint buffer to chunk storage',
            chunkIndex,
            this.waypointBuffer.length
        );
        await this.storageService.storeWaypointChunk(
            currentRide._id,
            chunkIndex - 1,
            this.waypointBuffer
        );

        console.log('Flushing waypoint buffer to chunk storage');
        this.waypointBuffer = [];
    }

    // Modified addWayPoint to use buffer
    public async addWayPoint(location: Location): Promise<void> {
        const currentRide = this.currentRideSubject.value;
        if (!currentRide || currentRide.appRideStatus !== AppRideStatus.Riding) {
            return;
        }
        try {
            const lastWayPoint =
                currentRide.paths?.[0]?.wayPoints?.slice(-1)[0] ||
                this.waypointBuffer[this.waypointBuffer.length - 1];
            const newWayPoint = new WayPoint({
                timestamp: new Date(location.time || Date.now()),
                latitude: location.latitude,
                longitude: location.longitude,
                altitude: location.altitude,
                distanceInKilometers: 0
            });
            // Skip if duplicate
            if (
                lastWayPoint &&
                lastWayPoint.longitude === newWayPoint.longitude &&
                lastWayPoint.latitude === newWayPoint.latitude
            ) {
                return;
            }
            if (lastWayPoint) {
                const distance =
                    (lastWayPoint.distanceInKilometers || 0) +
                    wayPointKilometers(lastWayPoint, newWayPoint);
                newWayPoint.distanceInKilometers =
                    isNaN(distance) || distance < 0
                        ? lastWayPoint.distanceInKilometers || 0
                        : distance;
            }
            // Add to buffer
            console.log('Adding waypoint to buffer', this.addWaypointCalled);
            this.waypointBuffer.push(newWayPoint);
            this.addWaypointCalled++;

            // --- NEW: Also update the in-memory ride object for UI ---
            if (!currentRide.paths?.length) {
                currentRide.paths = [
                    {
                        startDateTime: currentRide.startDateTime,
                        endDateTime: undefined,
                        wayPoints: []
                    }
                ];
            } else {
                // Keep paths structure but clear waypoints as they're in chunks
                currentRide.paths[0].wayPoints.push(...this.waypointBuffer);
            }

            // Update distance and duration
            currentRide.distanceKilometers = newWayPoint.distanceInKilometers;
            const now = Date.now();
            const startTime = currentRide.startDateTime.getTime();
            const pauseTime = isNaN(currentRide.pauseTimeInSeconds)
                ? 0
                : currentRide.pauseTimeInSeconds;
            const calculatedDuration = (now - startTime) / 1000 - pauseTime;
            currentRide.rideDuration = Math.max(0, calculatedDuration);
            // Emit for UI
            this.currentRideSubject.next(currentRide);

            // Flush if buffer is full
            if (this.waypointBuffer.length >= this.WAYPOINT_BUFFER_SIZE) {
                console.log('Flushing waypoint buffer');
                await this.flushWaypointBuffer();
            }

            this.rideDirty = true;
        } catch (error) {
            console.error('Failed to buffer waypoint:', error);
        }
    }

    public async pauseRide(): Promise<void> {
        // Flush buffer before pausing to avoid data loss
        await this.flushWaypointBuffer(true);
        const currentRide = this.currentRideSubject.value;
        if (!currentRide) {
            throw RideSyncError.noCurrentRide();
        }
        const lastWayPoint = this.getCurrentRideLastWaypoint();
        if (currentRide.appRideStatus !== AppRideStatus.Riding) {
            throw RideSyncError.invalidStatus(currentRide.appRideStatus, AppRideStatus.Riding);
        }
        // Calculate pause time
        const now = Date.now();
        const pauseStart = now;
        const startTime = currentRide.startDateTime.getTime();
        const currentPause = isNaN(currentRide.pauseTimeInSeconds)
            ? 0
            : currentRide.pauseTimeInSeconds;
        // Accumulate pause time
        currentRide.appRideStatus = AppRideStatus.Paused;
        const calculatedPauseTime =
            currentPause + ((pauseStart - startTime) / 1000 - currentRide.rideDuration);
        currentRide.pauseTimeInSeconds = Math.max(0, calculatedPauseTime);
        if (lastWayPoint) {
            currentRide.pausedWaypoints.push(lastWayPoint);
        }
        this.currentRideSubject.next(currentRide);
        this.rideDirty = true;
    }

    public async resumeRide(): Promise<void> {
        const currentRide = this.currentRideSubject.value;
        if (!currentRide) {
            throw RideSyncError.noCurrentRide();
        }
        if (currentRide.appRideStatus !== AppRideStatus.Paused) {
            throw RideSyncError.invalidStatus(currentRide.appRideStatus, AppRideStatus.Paused);
        }
        // Resume ride: just set status and keep pauseTimeInSeconds as is
        currentRide.appRideStatus = AppRideStatus.Riding;
        await this.storageService.setUserData('currentRide', currentRide);
        this.currentRideSubject.next(currentRide);
        this.rideDirty = true;
    }

    public async endRide(): Promise<void> {
        // Flush buffer before ending to avoid data loss
        await this.flushWaypointBuffer(true);
        this.addWaypointCalled = 0;
        const currentRide = this.currentRideSubject.value;
        if (!currentRide) {
            throw RideSyncError.noCurrentRide();
        }
        try {
            if (
                currentRide.appRideStatus !== AppRideStatus.Riding &&
                currentRide.appRideStatus !== AppRideStatus.Paused
            ) {
                throw RideSyncError.invalidStatus(currentRide.appRideStatus, [
                    AppRideStatus.Riding,
                    AppRideStatus.Paused
                ]);
            }

            // Update ride status and end time
            currentRide.appRideStatus = AppRideStatus.EndRideDetails;
            currentRide.endDateTime = new Date();

            // Ensure all paths have end times
            currentRide.paths = currentRide.paths.map(path => ({
                ...path,
                endDateTime: path.endDateTime || currentRide.endDateTime
            }));

            currentRide.horseIdentities.forEach(horse => {
                const gaitMetricsMinutes = new RideGaitMetrics({
                    horseId: horse._id,
                    metrics: getRideMinutesPerGait(
                        currentRide.paths[0].wayPoints,
                        currentRide.pausedWaypoints,
                        horse.gaitsKilometersPerHourSnapshot
                    )
                });
                currentRide.calculatedGaitMinutes.push(gaitMetricsMinutes);

                const gaitMetricsKilometers = new RideGaitMetrics({
                    horseId: horse._id,
                    metrics: getRideKilometersPerGait(
                        currentRide.paths[0].wayPoints,
                        currentRide.pausedWaypoints,
                        horse.gaitsKilometersPerHourSnapshot
                    )
                });
                currentRide.calculatedGaitKilometers.push(gaitMetricsKilometers);
            });

            // Store waypoints separately to ensure they're not lost
            await this.storageService.storeUnsyncedWaypoints(
                currentRide._id,
                currentRide.paths.reduce(
                    (acc, path) => [...acc, ...path.wayPoints],
                    [] as WayPoint[]
                )
            );
            // Store the ride locally first
            await this.storageService.storeUnsyncedRide(currentRide);
            await this.storageService.setUserData('currentRide', currentRide);
            this.currentRideSubject.next(currentRide);
            // Add to sync queue with high priority
            this._addToSyncQueue(currentRide._id, 'high');
            this.rideDirty = true;
        } catch (error) {
            console.error('[RideSync] Error in endRide:', error);
            throw error;
        }
    }

    public async saveRideDetails(details: {
        manualEntry?: { duration: number; distance: number };
        category: RideCategory;
        notes: string;
    }): Promise<CurrentRide> {
        // Flush buffer before finalizing to avoid data loss
        await this.flushWaypointBuffer(true);
        const currentRide = this.currentRideSubject.value;
        if (!currentRide) {
            throw RideSyncError.noCurrentRide();
        }
        if (currentRide.appRideStatus !== AppRideStatus.EndRideDetails) {
            throw RideSyncError.invalidStatus(
                currentRide.appRideStatus,
                AppRideStatus.EndRideDetails
            );
        }
        try {
            if (details.manualEntry) {
                currentRide.entryType = RideEntryType.Manual;

                const { duration, distance } = details.manualEntry;
                if (!isNil(duration)) {
                    currentRide.endDateTime = moment(currentRide.startDateTime)
                        .add(duration, 'milliseconds')
                        .toDate();
                }

                if (!isNil(distance)) {
                    currentRide.distanceKilometers = Math.max(0, distance);
                }
            }

            currentRide.category = details.category;
            currentRide.notes = details.notes;
            currentRide.appRideStatus = AppRideStatus.Finalized;

            await this.storageService.setUserData('currentRide', currentRide);
            this.currentRideSubject.next(currentRide);

            try {
                // First save to cache
                await firstValueFrom(this.rideCache.saveRideDetails(currentRide));

                // Clear current ride
                this.currentRideSubject.next(null);

                // Clear from storage
                await this.storageService.setUserData('currentRide', null);

                return currentRide;
            } catch (error) {
                console.error('[RideSync] Error saving to cache, storing as unsynced:', error);
                await this.storageService.storeUnsyncedRide(currentRide);
                await this.storageService.setUserData('currentRide', null);
                return currentRide; // Still return the ride for the share modal
            }
        } catch (error) {
            console.error('[RideSync] Error in saveRideDetails:', error);
            throw error;
        }
    }

    public async cancelRide(): Promise<void> {
        this.addWaypointCalled = 0;
        this.deleteRide(this.currentRideSubject.value?._id.toString());
    }

    public async clearCurrentRide(): Promise<void> {
        const currentRide = this.currentRideSubject.value;
        if (currentRide) {
            // First set the subject to null to prevent any race conditions
            this.currentRideSubject.next(null);

            // Then clear all storage data (including waypoint chunks)
            await this.storageService.clearCurrentRideData();

            // Reset chunk index
            this.waypointChunkIndex = 0;
        }
    }

    public getCurrentRide(): CurrentRide | null {
        return this.currentRideSubject.value;
    }

    public getCurrentRide$(): Observable<CurrentRide | null> {
        return this.currentRide$;
    }

    public async didShowGpsWarning(): Promise<void> {
        const currentRide = this.currentRideSubject.value;
        if (!currentRide) {
            throw RideSyncError.noCurrentRide();
        }

        currentRide.didShowGpsWarning = true;
        await this.storageService.setUserData('currentRide', currentRide);
        this.currentRideSubject.next(currentRide);
    }

    public getWayPoints(): Observable<WayPoint[] | null> {
        return this.currentRide$.pipe(
            map(ride => {
                if (!ride) {
                    return null;
                }
                return flatMap(ride.paths, path => path.wayPoints);
            })
        );
    }

    public async getCurrentRideWaypoints(): Promise<WayPoint[]> {
        const currentRide = this.currentRideSubject.value;
        if (!currentRide || !currentRide._id) {
            return [];
        }

        // Merge waypoint chunks (these are flushed waypoints)
        const waypointChunks = await this.storageService.getWaypointChunks(currentRide._id);

        // Also include any waypoints currently in paths (for immediate UI updates, not yet flushed)
        const pathWaypoints = currentRide.paths?.[0]?.wayPoints || [];

        // Combine chunks and current path waypoints
        // Path waypoints are the most recent ones that haven't been flushed yet
        return [...waypointChunks, ...pathWaypoints];
    }

    public async deleteRide(rideId: string): Promise<void> {
        const currentRide = this.currentRideSubject.value;
        if (!currentRide || currentRide._id !== rideId) {
            throw RideSyncError.noCurrentRide();
        }

        // Clear waypoint chunks before clearing ride
        await this.storageService.clearWaypointChunks(rideId);

        if (this.networkStatus.currentNetworkStatus.isOnline) {
            try {
                await firstValueFrom(
                    this.rideService.deleteRide(rideId).pipe(
                        take(1),
                        catchError(() => {
                            return of(null);
                        })
                    )
                );

                await this.clearCurrentRide();
            } catch (error) {
                await this.clearCurrentRide();
            }
        } else {
            this.clearCurrentRide();
        }
    }

    private getCurrentRideLastWaypoint(): WayPoint {
        const currentRide = this.currentRideSubject.value;
        if (!currentRide) {
            throw RideSyncError.noCurrentRide();
        }
        return currentRide.paths?.[0]?.wayPoints?.slice(-1)[0];
    }
}
